import { BoardID } from 'BoardID';
import { DEFAULT_SETTINGS } from 'Constants';
import { fromLocalStorageSettings, toLocalStorageSettings } from 'localStorage';
import {
  atom,
  AtomEffect,
  atomFamily,
  DefaultValue,
  selectorFamily,
} from 'recoil';

import BoardBase from 'boardClasses/BoardBase';
import { isEqual } from 'utils/utils';

const localStorageEffect: <T, F>(
  key: string,
  toLS: (to: T) => F,
  fromLS: (from: F) => T
) => AtomEffect<T> =
  (key, toLS, fromLS) =>
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue != null) {
      const parsed = JSON.parse(savedValue);
      setSelf(fromLS(parsed));
    }

    onSet((newValue) => {
      if (newValue instanceof DefaultValue) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(toLS(newValue)));
      }
    });
  };

export const routerMatchAtom = atom({
  key: 'routerMatchAtom',
  default: {
    isExact: false,
    path: '',
    url: '',
    params: {
      boardHash: '',
      boardID: '',
    },
  },
});

export const selectedBoardIDAtom = atom<BoardID>({
  key: 'selectedBoardIDAtom',
  default: '' as BoardID,
});

export const localBoardIDsAtom = atom<Array<BoardID>>({
  key: 'localBoardIDsAtom',
  default: [],
  effects_UNSTABLE: [
    localStorageEffect(
      'localBoardIDsAtom',
      (_) => _,
      (_) => _
    ),
  ],
});

export const cloudBoardIDsAtom = atom<Array<BoardID>>({
  key: 'cloudBoardIDsAtom',
  default: [],
});

export const boardInstanceAtomFamily = atomFamily<BoardBase | null, BoardID>({
  key: 'boardInstanceAtomFamily',
  default: null,
});

export const workingBoardInstanceAtomFamily = atomFamily<
  BoardBase | null,
  BoardID
>({
  key: 'workingBoardInstanceAtomFamily',
  default: null,
});

export const boardUndoStackAtomFamily = atomFamily<Array<BoardBase>, BoardID>({
  key: 'boardUndoStackAtomFamily',
  default: [],
});

export const isNewModalOpenAtom = atom({
  key: 'isNewModalOpenAtom',
  default: false,
});

export const settingsAtom = atom({
  key: 'settingsAtom',
  default: DEFAULT_SETTINGS,
  effects_UNSTABLE: [
    localStorageEffect(
      'settings',
      toLocalStorageSettings,
      fromLocalStorageSettings
    ),
  ],
});

export const isOnlineAtom = atom({
  key: 'isOnlineAtom',
  default: true,
});

export const selectAreChangesPending = selectorFamily({
  key: 'selectAreChangesPending',
  get:
    (boardID: BoardID) =>
    ({ get }) => {
      const board = get(boardInstanceAtomFamily(boardID));

      if (board == null) {
        return true;
      }

      const workingBoard = get(workingBoardInstanceAtomFamily(boardID));

      if (workingBoard == null) {
        return false;
      }

      return !isEqual(board, workingBoard);
    },
});

export const selectIsLocalBoard = selectorFamily({
  key: 'selectIsLocalBoard',
  get:
    (boardID: BoardID) =>
    ({ get }) => {
      return get(localBoardIDsAtom).indexOf(boardID) !== -1;
    },
});

export const selectBoardName = selectorFamily({
  key: 'selectBoardName',
  get:
    (boardID: BoardID) =>
    ({ get }) => {
      return get(boardInstanceAtomFamily(boardID))?.name ?? null;
    },
});

export const selectBoardType = selectorFamily({
  key: 'selectBoardName',
  get:
    (boardID: BoardID) =>
    ({ get }) => {
      return get(boardInstanceAtomFamily(boardID))?.type ?? null;
    },
});
